import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Disc3, Upload, BadgeCheck, AlertTriangle, User, Sun, Moon, ScanLine, Shovel } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import Avatar from '@/components/Avatar';
import BrandWordmark from '@/components/BrandWordmark';
import ScanPassModal from '@/components/ScanPassModal';
import DigSessionModal from '@/components/DigSessionModal';

const tabs = [
  { to: '/crate', label: 'Collection', icon: Disc3, testid: 'nav-crate' },
  { to: '/upload', label: 'Scan', icon: Upload, testid: 'nav-upload' },
  { to: '/owned', label: 'Owned', icon: BadgeCheck, testid: 'nav-owned' },
  { to: '/unidentified', label: 'Fix', icon: AlertTriangle, testid: 'nav-unidentified' },
  { to: '/profile', label: 'Profile', icon: User, testid: 'nav-profile' },
];

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [scanPassOpen, setScanPassOpen] = useState(false);
  const [passInitialToken, setPassInitialToken] = useState(null);
  const [digOpen, setDigOpen] = useState(false);
  const [digInitialCode, setDigInitialCode] = useState(null);
  const [digInitialMode, setDigInitialMode] = useState('chooser');

  // Deep-link: /pass/{token} → auto-open the scanner with the token pre-loaded.
  // This is triggered when a GroovLenser scans a QR with their phone's native
  // camera app (which opens the URL in a browser) and lands on the app.
  useEffect(() => {
    const passMatch = location.pathname.match(/^\/pass\/([A-Za-z0-9_-]+)$/);
    if (passMatch && user) {
      setPassInitialToken(passMatch[1]);
      setScanPassOpen(true);
      navigate('/crate', { replace: true });
      return;
    }
    // Deep-link: /dig/{code} → auto-open the Dig modal as guest with the
    // code pre-filled (typically scanned from the host's QR).
    const digMatch = location.pathname.match(/^\/dig\/([A-Za-z0-9]+)$/);
    if (digMatch && user) {
      setDigInitialCode(digMatch[1].toUpperCase());
      setDigInitialMode('guest');
      setDigOpen(true);
      navigate('/crate', { replace: true });
    }
  }, [location.pathname, user, navigate]);
  return (
    // NOTE: Do NOT add `overflow-x-hidden` or `overscroll-*` here.
    // Doing so makes this div an implicit scroll container (per CSS spec:
    // `overflow-x: hidden` forces `overflow-y: auto`), which then competes
    // with the browser viewport for wheel events. On laptop trackpads the
    // deltas get lost between the two containers and the page stops
    // scrolling with the trackpad even though touch/keyboard scrolling
    // still work. Horizontal overflow protection lives on <html>/<body>
    // in index.css instead.
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Top bar (desktop + mobile)
          `viewport-fit=cover` + iOS notch/dynamic island means the top 47px
          of the viewport can sit BEHIND the notch. We pad with
          `env(safe-area-inset-top)` so the logo/nav drop below it.
          Horizontal safe-area insets handle landscape rounded corners + some
          phones that have a curved edge in portrait. */}
      <header
        className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/crate" className="flex items-center" data-testid="app-logo">
            <BrandWordmark size="md" />
          </Link>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-lg text-sm transition-colors duration-200 ${
                    isActive
                      ? 'bg-secondary text-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  }`
                }
              >
                <span className="inline-flex items-center gap-1.5">
                  <t.icon className="w-4 h-4" />
                  {t.label}
                </span>
              </NavLink>
            ))}
          </nav>

          {/* Right cluster: scan pass + theme toggle + avatar */}
          <div className="flex items-center gap-2">
            {user && (
              <button
                type="button"
                onClick={() => {
                  setDigInitialCode(null);
                  setDigInitialMode('chooser');
                  setDigOpen(true);
                }}
                data-testid="nav-dig-mode"
                aria-label="Start or join a Dig session"
                title="Dig Mode — browse a fellow GroovLenser's crates"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[hsl(var(--primary))] hover:text-white hover:bg-[hsl(var(--primary))]/25 transition-colors"
              >
                <Shovel className="w-4 h-4" />
              </button>
            )}
            {user && (
              <button
                type="button"
                onClick={() => setScanPassOpen(true)}
                data-testid="nav-scan-pass"
                aria-label="Scan a Pass from another GroovLenser"
                title="Scan a Pass"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-[hsl(var(--primary))] hover:text-white hover:bg-[hsl(var(--primary))]/25 transition-colors"
              >
                <ScanLine className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={toggle}
              data-testid="nav-theme-toggle"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Link
              to="/profile"
              data-testid="nav-avatar"
              aria-label="Open profile"
              className="hidden md:flex"
            >
              <Avatar user={user} size={32} />
            </Link>
          </div>
        </div>
      </header>

      {/* Page content
          On mobile we leave room for the fixed bottom nav (~64px) PLUS the
          iOS home-indicator safe-area inset (~34px) so the last row of
          content isn't covered. On desktop there's no bottom nav so we use
          a much smaller bottom padding. Horizontal safe-area insets handle
          landscape rounded corners. */}
      <main
        className="flex-1 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-10"
        key={location.pathname}
        style={{
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <Outlet />
        </div>
      </main>

      {/* Bottom tab bar (mobile)
          z-[10000] keeps it above the third-party "Made with Emergent" badge
          (which is fixed at z-index: 9999). When a Radix Dialog opens, the
          nav is hidden via the [data-scroll-locked] CSS rule in index.css
          so it does not cover modal content.
          Bottom safe-area inset lifts the nav above the iOS home indicator. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-[10000] border-t border-border bg-background/95 backdrop-blur"
        aria-label="Bottom navigation"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      >
        <ul className="grid grid-cols-5">
          {tabs.map((t) => (
            <li key={t.to}>
              <NavLink
                to={t.to}
                data-testid={`${t.testid}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] ${
                    isActive ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'
                  }`
                }
              >
                <t.icon className="w-5 h-5" />
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Scan-a-Pass modal — global handoff receiver, accessible from every
          page via the header ScanLine button. Also auto-opens with a
          pre-filled token when the user follows a /pass/{token} deep-link
          (e.g. from a QR scan opened via the phone's native camera). */}
      <ScanPassModal
        open={scanPassOpen}
        onClose={() => {
          setScanPassOpen(false);
          setPassInitialToken(null);
        }}
        initialToken={passInitialToken}
      />

      {/* Dig Mode modal — live browse-each-other-crates session. Accessible
          from the header Shovel icon (chooser) or auto-opened as guest via
          /dig/{code} deep-link. */}
      <DigSessionModal
        open={digOpen}
        onClose={() => {
          setDigOpen(false);
          setDigInitialCode(null);
          setDigInitialMode('chooser');
        }}
        initialCode={digInitialCode}
        initialMode={digInitialMode}
      />
    </div>
  );
}
